export type ImageUploadPayload = {
    dataBase64: string;
    fileName: string;
    mimeType: string;
    size: number;
};

export type ImageCropUploadValue = {
    imageName: string;
    imageUrl: string;
    imageUpload?: ImageUploadPayload;
};

export type CropSize = {
    width: number;
    height: number;
};

export type CropRect = {
    x: number;
    y: number;
    width: number;
    height: number;
};

export type Size = {
    width: number;
    height: number;
};

export type FileValidationResult =
    { ok: true } | { ok: false; reason: 'type' | 'size' };

export const SUPPORTED_IMAGE_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
] as const;

export type SupportedImageMimeType =
    (typeof SUPPORTED_IMAGE_MIME_TYPES)[number];

export const SUPPORTED_IMAGE_ACCEPT = SUPPORTED_IMAGE_MIME_TYPES.join(',');

export const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

export const DEFAULT_CROP_SIZE: CropSize = { width: 1280, height: 720 };

export const CROP_MIN_DISPLAYED_SIZE = 32;

export const isSupportedImageType = (
    type: string,
): type is SupportedImageMimeType =>
    (SUPPORTED_IMAGE_MIME_TYPES as readonly string[]).includes(type);

export const validateImageFile = (file: File): FileValidationResult => {
    if (!isSupportedImageType(file.type)) {
        return { ok: false, reason: 'type' };
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
        return { ok: false, reason: 'size' };
    }
    return { ok: true };
};

export const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error('read-failed'));
        reader.readAsDataURL(file);
    });

export const dataUrlToBase64 = (dataUrl: string): string => {
    const commaIndex = dataUrl.indexOf(',');
    return commaIndex === -1 ? dataUrl : dataUrl.slice(commaIndex + 1);
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('image-load-failed'));
        image.src = src;
    });

export const loadImageSize = async (src: string): Promise<Size> => {
    const image = await loadImage(src);
    return { width: image.naturalWidth, height: image.naturalHeight };
};

export const getCropAspectRatio = (cropSize: CropSize): number =>
    cropSize.width / cropSize.height;

export const computeInitialCropRect = (
    displayedSize: Size,
    aspectRatio: number,
): CropRect => {
    let width = displayedSize.width;
    let height = width / aspectRatio;

    if (height > displayedSize.height) {
        height = displayedSize.height;
        width = height * aspectRatio;
    }

    return {
        x: (displayedSize.width - width) / 2,
        y: (displayedSize.height - height) / 2,
        width,
        height,
    };
};

const clamp = (value: number, min: number, max: number): number =>
    Math.min(Math.max(value, min), max);

export const constrainCropPosition = (
    rect: CropRect,
    displayedSize: Size,
): CropRect => ({
    ...rect,
    x: clamp(rect.x, 0, Math.max(0, displayedSize.width - rect.width)),
    y: clamp(rect.y, 0, Math.max(0, displayedSize.height - rect.height)),
});

export const resizeCropRect = (
    rect: CropRect,
    nextWidth: number,
    displayedSize: Size,
    aspectRatio: number,
): CropRect => {
    const maxWidthByX = displayedSize.width - rect.x;
    const maxWidthByY = (displayedSize.height - rect.y) * aspectRatio;
    const maxWidth = Math.min(maxWidthByX, maxWidthByY);
    const minWidth = Math.min(CROP_MIN_DISPLAYED_SIZE * aspectRatio, maxWidth);

    const width = clamp(nextWidth, minWidth, maxWidth);
    const height = width / aspectRatio;

    return { ...rect, width, height };
};

export const getCropNaturalDimensions = (
    rect: CropRect,
    displayedSize: Size,
    naturalSize: Size,
): Size => {
    if (displayedSize.width === 0 || displayedSize.height === 0) {
        return { width: 0, height: 0 };
    }

    const scaleX = naturalSize.width / displayedSize.width;
    const scaleY = naturalSize.height / displayedSize.height;

    return {
        width: Math.round(rect.width * scaleX),
        height: Math.round(rect.height * scaleY),
    };
};

export const cropImageToDataUrl = async ({
    source,
    rect,
    displayedSize,
    naturalSize,
    cropSize,
    mimeType,
}: {
    source: string;
    rect: CropRect;
    displayedSize: Size;
    naturalSize: Size;
    cropSize: CropSize;
    mimeType: string;
}): Promise<string> => {
    const image = await loadImage(source);

    const scaleX = naturalSize.width / displayedSize.width;
    const scaleY = naturalSize.height / displayedSize.height;

    const canvas = document.createElement('canvas');
    canvas.width = cropSize.width;
    canvas.height = cropSize.height;

    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('canvas-context-unavailable');
    }

    context.drawImage(
        image,
        rect.x * scaleX,
        rect.y * scaleY,
        rect.width * scaleX,
        rect.height * scaleY,
        0,
        0,
        cropSize.width,
        cropSize.height,
    );

    return canvas.toDataURL(mimeType);
};
